import { FC, useState, useEffect } from 'react'
import styles from 'styles/components/modal.module.scss'
import SelectBoxWithLabel from 'components/Form/WithLabel/SelectBox'
import InputTextWithLabel from 'components/Form/WithLabel/InputText'
import InputTextAndUnitWithLabel from 'components/Form/WithLabel/InputTextAndUnit'
import RadioButtonWithLabel from 'components/Form/WithLabel/RadioButton'
import RadioButtonAndInputTextAndUnitWithLabel from 'components/Form/WithLabel/RadioButtonAndInputTextAndUnit'
import CheckBox from 'components/Form/CheckBox'
import {
  BulkBasicInfoField,
  BreadRepresentationLawName,
  BreadRepresentationLawNameLabel,
} from 'reducks/bread/slice'

export type BulkBasicInfoSubmitPayload = {
  // 有効化（チェック）された項目のみを対象に部分更新する
  fields: BulkBasicInfoField[]
  packaging_cost?: number | null
  preservation_method?: string
  is_salling?: boolean
  representation_law_name?: string
  representation_law_name_other?: string | null
  is_consumption_period?: boolean
  period_date?: number
  is_consumption_period_after_thawing?: boolean
  period_date_after_thawing?: number | null
}

interface Props {
  isOpen: boolean
  selectedCount: number
  isStandardPlan: boolean
  onCancel: () => void
  onSubmit: (payload: BulkBasicInfoSubmitPayload) => void
}

// 保存方法の選択肢（単品編集 BreadAddTemplate と揃える）
const PRESERVATION_METHOD_OPTIONS = [
  {
    is_default: true,
    label: '----',
    value: '',
  },
  {
    is_default: false,
    label: '直射日光・高温多湿を避けお早目にお召し上がり下さい。',
    value: 'avoid_sunlight',
  },
  {
    is_default: false,
    label: '要冷蔵（10℃以下で保存して下さい。）',
    value: 'refrigerated',
  },
  {
    is_default: false,
    label: '要冷凍（-18℃以下で保存して下さい。）',
    value: 'frozen',
  },
]

// 食品表示法上の名称の選択肢（単品編集 BreadAddTemplate と揃える）
const REPRESENTATION_LAW_NAME_OPTIONS = [
  {
    is_default: true,
    label: '----',
    value: '',
  },
  {
    is_default: false,
    label: BreadRepresentationLawNameLabel.RECTANGULAR_BREAD,
    value: BreadRepresentationLawName.RECTANGULAR_BREAD,
  },
  {
    is_default: false,
    label: BreadRepresentationLawNameLabel.SWEETENED_BUN,
    value: BreadRepresentationLawName.SWEETENED_BUN,
  },
  {
    is_default: false,
    label: BreadRepresentationLawNameLabel.BREAD,
    value: BreadRepresentationLawName.BREAD,
  },
  {
    is_default: false,
    label: BreadRepresentationLawNameLabel.BAKED_SWEETS,
    value: BreadRepresentationLawName.BAKED_SWEETS,
  },
  {
    is_default: false,
    label: BreadRepresentationLawNameLabel.WESTERN_SWEETS,
    value: BreadRepresentationLawName.WESTERN_SWEETS,
  },
  {
    is_default: false,
    label: BreadRepresentationLawNameLabel.FRESH_WESTERN_SWEETS,
    value: BreadRepresentationLawName.FRESH_WESTERN_SWEETS,
  },
  {
    is_default: false,
    label: BreadRepresentationLawNameLabel.OTHER,
    value: BreadRepresentationLawName.OTHER,
  },
]

const FIELD_LABELS: { [key in BulkBasicInfoField]: string } = {
  packaging_cost: '包材費',
  preservation_method: '保存方法',
  is_salling: '店頭販売状況',
  representation_law_name: '食品表示法上の名称',
  period_representation: '期限表示',
}

const BulkBasicInfoModal: FC<Props> = ({
  isOpen,
  selectedCount,
  isStandardPlan,
  onCancel,
  onSubmit,
}) => {
  // 各項目の有効化（チェック）状態
  const [enablePackagingCost, setEnablePackagingCost] = useState<boolean>(false)
  const [enablePreservationMethod, setEnablePreservationMethod] =
    useState<boolean>(false)
  const [enableIsSalling, setEnableIsSalling] = useState<boolean>(false)
  const [enableRepresentationLawName, setEnableRepresentationLawName] =
    useState<boolean>(false)
  const [enablePeriodRepresentation, setEnablePeriodRepresentation] =
    useState<boolean>(false)

  // 各項目の入力値
  const [packagingCost, setPackagingCost] = useState<string>('')
  const [preservationMethod, setPreservationMethod] = useState<string>('')
  const [isSalling, setIsSalling] = useState<boolean>(true)
  const [representationLawName, setRepresentationLawName] = useState<string>('')
  const [representationLawNameOther, setRepresentationLawNameOther] =
    useState<string>('')
  const [isConsumptionPeriod, setIsConsumptionPeriod] = useState<boolean>(true)
  const [periodDate, setPeriodDate] = useState<string>('')
  const [isConsumptionPeriodAfterThawing, setIsConsumptionPeriodAfterThawing] =
    useState<boolean>(true)
  const [periodDateAfterThawing, setPeriodDateAfterThawing] =
    useState<string>('')

  // 項目ごとのエラーメッセージ
  const [errors, setErrors] = useState<{
    [key in BulkBasicInfoField]?: string
  }>({})

  // モーダルを開くたびに入力状態を初期化する
  useEffect(() => {
    if (isOpen) {
      setEnablePackagingCost(false)
      setEnablePreservationMethod(false)
      setEnableIsSalling(false)
      setEnableRepresentationLawName(false)
      setEnablePeriodRepresentation(false)
      setPackagingCost('')
      setPreservationMethod('')
      setIsSalling(true)
      setRepresentationLawName('')
      setRepresentationLawNameOther('')
      setIsConsumptionPeriod(true)
      setPeriodDate('')
      setIsConsumptionPeriodAfterThawing(true)
      setPeriodDateAfterThawing('')
      setErrors({})
    }
  }, [isOpen])

  if (!isOpen) return null

  // チェックされている項目（包材費はスタンダードプランのみ対象）
  const enabledFields: BulkBasicInfoField[] = [
    ...(isStandardPlan && enablePackagingCost
      ? (['packaging_cost'] as BulkBasicInfoField[])
      : []),
    ...(enablePreservationMethod
      ? (['preservation_method'] as BulkBasicInfoField[])
      : []),
    ...(enableIsSalling ? (['is_salling'] as BulkBasicInfoField[]) : []),
    ...(enableRepresentationLawName
      ? (['representation_law_name'] as BulkBasicInfoField[])
      : []),
    ...(enablePeriodRepresentation
      ? (['period_representation'] as BulkBasicInfoField[])
      : []),
  ]

  const handleSubmit = () => {
    if (enabledFields.length === 0) {
      return
    }
    const nextErrors: { [key in BulkBasicInfoField]?: string } = {}
    const payload: BulkBasicInfoSubmitPayload = { fields: enabledFields }

    if (enabledFields.includes('packaging_cost')) {
      const trimmed = packagingCost.replace(/,/g, '').trim()
      if (trimmed === '') {
        nextErrors.packaging_cost = '包材費を入力してください。'
      } else {
        const num = Number(trimmed)
        if (Number.isNaN(num) || num < 0) {
          nextErrors.packaging_cost = '包材費は0以上の数値で入力してください。'
        } else {
          payload.packaging_cost = num
        }
      }
    }
    if (enabledFields.includes('preservation_method')) {
      if (!preservationMethod) {
        nextErrors.preservation_method = '保存方法を選択してください。'
      } else {
        payload.preservation_method = preservationMethod
      }
    }
    if (enabledFields.includes('is_salling')) {
      payload.is_salling = isSalling
    }
    if (enabledFields.includes('representation_law_name')) {
      if (!representationLawName) {
        nextErrors.representation_law_name =
          '食品表示法上の名称を選択してください。'
      } else if (
        representationLawName === BreadRepresentationLawName.OTHER &&
        !representationLawNameOther.trim()
      ) {
        nextErrors.representation_law_name =
          '食品表示法上の名称(その他)を入力してください。'
      } else {
        payload.representation_law_name = representationLawName
        payload.representation_law_name_other =
          representationLawName === BreadRepresentationLawName.OTHER
            ? representationLawNameOther.trim()
            : null
      }
    }
    if (enabledFields.includes('period_representation')) {
      const trimmed = periodDate.replace(/,/g, '').trim()
      const num = Number(trimmed)
      if (trimmed === '' || Number.isNaN(num) || num < 0) {
        nextErrors.period_representation =
          '期限表示の日数を0以上の数値で入力してください。'
      } else {
        payload.is_consumption_period = isConsumptionPeriod
        payload.period_date = num
        payload.is_consumption_period_after_thawing =
          isConsumptionPeriodAfterThawing
        const trimmedThawing = periodDateAfterThawing.replace(/,/g, '').trim()
        if (trimmedThawing === '') {
          payload.period_date_after_thawing = null
        } else {
          const numThawing = Number(trimmedThawing)
          if (Number.isNaN(numThawing) || numThawing < 0) {
            nextErrors.period_representation =
              '冷凍時期限表示の日数は0以上の数値で入力してください。'
          } else {
            payload.period_date_after_thawing = numThawing
          }
        }
      }
    }

    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors)
      return
    }

    const confirmed = window.confirm(
      `選択中の${selectedCount}件の商品の${enabledFields
        .map((f) => `「${FIELD_LABELS[f]}」`)
        .join('')}を一括で書き換えます。よろしいですか？`,
    )
    if (!confirmed) {
      return
    }
    onSubmit(payload)
  }

  // 項目の有効化チェックボックス付き枠
  const fieldSectionStyle = {
    border: '1px solid #E2E8F0',
    borderRadius: '6px',
    padding: '16px',
    marginBottom: '16px',
  }
  return (
    <div className={styles.modalOverlay} onClick={onCancel}>
      <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        <div
          className={styles.modalHeader}
          style={{ backgroundColor: 'rgba(226, 232, 240, 0.5)' }}
        >
          <h2>商品基本情報の一括設定</h2>
        </div>

        <div className={styles.modalBody} style={{ backgroundColor: '#fff' }}>
          <p
            style={{
              marginBottom: '20px',
              fontSize: '14px',
              color: '#4A5568',
            }}
          >
            選択中の<b>{selectedCount}件</b>
            の商品に対して、設定したい項目に
            <b>チェック</b>
            を入れて値を編集してください。複数項目を同時に設定できます。
          </p>

          {/* 包材費（スタンダードプランのみ） */}
          {isStandardPlan && (
            <div style={fieldSectionStyle}>
              <CheckBox
                isChecked={enablePackagingCost}
                label="包材費を設定する"
                size="middle"
                type="default"
                onChange={() => {
                  setEnablePackagingCost(!enablePackagingCost)
                  setErrors((prev) => ({
                    ...prev,
                    packaging_cost: undefined,
                  }))
                }}
              />
              {enablePackagingCost && (
                <div style={{ marginTop: '12px' }}>
                  <InputTextAndUnitWithLabel
                    required={true}
                    labelName="包材費"
                    description=""
                    line={1}
                    type="text"
                    size="small"
                    value={packagingCost}
                    unit="円"
                    unitDescription="(税込)"
                    placeholder="0,000"
                    errorText={errors.packaging_cost ?? null}
                    onChange={(event) => {
                      setPackagingCost(event.target.value)
                      setErrors((prev) => ({
                        ...prev,
                        packaging_cost: undefined,
                      }))
                    }}
                  />
                </div>
              )}
            </div>
          )}

          {/* 保存方法 */}
          <div style={fieldSectionStyle}>
            <CheckBox
              isChecked={enablePreservationMethod}
              label="保存方法を設定する"
              size="middle"
              type="default"
              onChange={() => {
                setEnablePreservationMethod(!enablePreservationMethod)
                setErrors((prev) => ({
                  ...prev,
                  preservation_method: undefined,
                }))
              }}
            />
            {enablePreservationMethod && (
              <div style={{ marginTop: '12px' }}>
                <SelectBoxWithLabel
                  required={true}
                  labelName="保存方法"
                  description="保存方法を選択してください。"
                  line={1}
                  size="large"
                  value={preservationMethod}
                  options={PRESERVATION_METHOD_OPTIONS}
                  errorText={errors.preservation_method ?? null}
                  onChange={(event) => {
                    setPreservationMethod(event.target.value)
                    setErrors((prev) => ({
                      ...prev,
                      preservation_method: undefined,
                    }))
                  }}
                />
              </div>
            )}
          </div>

          {/* 店頭販売状況 */}
          <div style={fieldSectionStyle}>
            <CheckBox
              isChecked={enableIsSalling}
              label="店頭販売状況を設定する"
              size="middle"
              type="default"
              onChange={() => setEnableIsSalling(!enableIsSalling)}
            />
            {enableIsSalling && (
              <div style={{ marginTop: '12px' }}>
                <RadioButtonWithLabel
                  required={true}
                  labelName="店頭販売状況"
                  description="店頭販売状況を選択してください。"
                  line={1}
                  checkInfos={[
                    {
                      isChecked: isSalling,
                      label: '販売中',
                      onChange: () => setIsSalling(true),
                    },
                    {
                      isChecked: !isSalling,
                      label: '販売停止',
                      onChange: () => setIsSalling(false),
                    },
                  ]}
                />
              </div>
            )}
          </div>

          {/* 食品表示法上の名称 */}
          <div style={fieldSectionStyle}>
            <CheckBox
              isChecked={enableRepresentationLawName}
              label="食品表示法上の名称を設定する"
              size="middle"
              type="default"
              onChange={() => {
                setEnableRepresentationLawName(!enableRepresentationLawName)
                setErrors((prev) => ({
                  ...prev,
                  representation_law_name: undefined,
                }))
              }}
            />
            {enableRepresentationLawName && (
              <div style={{ marginTop: '12px' }}>
                <SelectBoxWithLabel
                  required={true}
                  labelName="食品表示法上の名称"
                  description="裏貼りシール（食品表示ラベル）に記載する名称を選択してください。"
                  line={2}
                  size="large"
                  value={representationLawName}
                  options={REPRESENTATION_LAW_NAME_OPTIONS}
                  errorText={errors.representation_law_name ?? null}
                  onChange={(event) => {
                    setRepresentationLawName(event.target.value)
                    setErrors((prev) => ({
                      ...prev,
                      representation_law_name: undefined,
                    }))
                  }}
                />
                {representationLawName === BreadRepresentationLawName.OTHER && (
                  <InputTextWithLabel
                    required={true}
                    labelName="食品表示法上の名称(その他)"
                    description="裏貼りシール（食品表示ラベル）に記載する名称を入力してください。"
                    line={1}
                    type="text"
                    size="large"
                    value={representationLawNameOther}
                    placeholder="食パン"
                    onChange={(event) => {
                      setRepresentationLawNameOther(event.target.value)
                      setErrors((prev) => ({
                        ...prev,
                        representation_law_name: undefined,
                      }))
                    }}
                  />
                )}
              </div>
            )}
          </div>

          {/* 期限表示 */}
          <div style={fieldSectionStyle}>
            <CheckBox
              isChecked={enablePeriodRepresentation}
              label="期限表示を設定する"
              size="middle"
              type="default"
              onChange={() => {
                setEnablePeriodRepresentation(!enablePeriodRepresentation)
                setErrors((prev) => ({
                  ...prev,
                  period_representation: undefined,
                }))
              }}
            />
            {enablePeriodRepresentation && (
              <div style={{ marginTop: '12px' }}>
                <RadioButtonAndInputTextAndUnitWithLabel
                  required={true}
                  labelName="期限表示"
                  description="消費期限か賞味期限を選択後、日数を入力してください。"
                  line={1}
                  checkInfos={[
                    {
                      isChecked: isConsumptionPeriod,
                      label: '消費期限',
                      onChange: () => setIsConsumptionPeriod(true),
                    },
                    {
                      isChecked: !isConsumptionPeriod,
                      label: '賞味期限',
                      onChange: () => setIsConsumptionPeriod(false),
                    },
                  ]}
                  type="text"
                  size="xsmall"
                  value={periodDate}
                  placeholder="製造から00"
                  unit="日"
                  errorText={errors.period_representation ?? null}
                  onChange={(event) => {
                    setPeriodDate(event.target.value)
                    setErrors((prev) => ({
                      ...prev,
                      period_representation: undefined,
                    }))
                  }}
                />
                <RadioButtonAndInputTextAndUnitWithLabel
                  required={false}
                  labelName="冷凍時期限表示"
                  description="消費期限か賞味期限を選択後、日数を入力してください。（任意）"
                  line={1}
                  checkInfos={[
                    {
                      isChecked: isConsumptionPeriodAfterThawing,
                      label: '消費期限',
                      onChange: () => setIsConsumptionPeriodAfterThawing(true),
                    },
                    {
                      isChecked: !isConsumptionPeriodAfterThawing,
                      label: '賞味期限',
                      onChange: () => setIsConsumptionPeriodAfterThawing(false),
                    },
                  ]}
                  type="text"
                  size="xsmall"
                  value={periodDateAfterThawing}
                  placeholder="製造から00"
                  unit="日"
                  onChange={(event) => {
                    setPeriodDateAfterThawing(event.target.value)
                    setErrors((prev) => ({
                      ...prev,
                      period_representation: undefined,
                    }))
                  }}
                />
              </div>
            )}
          </div>
        </div>

        <div className={styles.modalFooter}>
          <button
            onClick={onCancel}
            style={{
              padding: '12px 24px',
              fontSize: '14px',
              backgroundColor: '#fff',
              border: '1px solid #E2E8F0',
              borderRadius: '4px',
              cursor: 'pointer',
              marginRight: '12px',
              color: '#4A5568',
              fontWeight: '500',
            }}
          >
            キャンセル
          </button>
          <button
            onClick={handleSubmit}
            disabled={enabledFields.length === 0}
            style={{
              padding: '12px 24px',
              fontSize: '14px',
              backgroundColor: enabledFields.length > 0 ? '#F4A460' : '#E2E8F0',
              color: '#fff',
              border: 'none',
              borderRadius: '4px',
              cursor: enabledFields.length > 0 ? 'pointer' : 'not-allowed',
              fontWeight: 'bold',
            }}
          >
            一括設定する
          </button>
        </div>
      </div>
    </div>
  )
}

export default BulkBasicInfoModal
